import { useMemo, useState } from 'react';
import { FlaskConical, Gift } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@ncoder/ui';
import {
  OrgAdminEmptyState,
  OrgAdminErrorState,
} from '@/components/molecules/org-admin/OrgAdminStates';
import { ReferralDialog } from '@/components/molecules/org-admin/ReferralDialog';
import { RewardCard } from '@/components/molecules/org-admin/RewardCard';
import {
  useMarkRewardPending,
  useReferralProgram,
  useSubmitReferral,
} from '@/hooks/org-admin/useReferralProgram';
import { cn } from '@/lib/utils';
import type { ReferralRecord, ReferralSubmission } from '@/services/org-admin/referrals';
import { REFERRAL_PROGRAM_IS_MOCK } from '@/services/org-admin/referrals';
import { OrgUsageStatRail, type StatSpec } from './OrgUsageStatRail';
import { formatRewardUsd, rewardLabel } from '@/components/molecules/org-admin/RewardCard';

/**
 * Rewards — the referral programme.
 *
 * Placed as a tab in the Admin panel rather than a sidebar entry, for two reasons. The reward is a
 * credit against the bill, which puts it next to Usage and Analysis — the admin reading "$465
 * remaining this cycle" is exactly the person who wants "$250 off". And the Admin panel is already
 * gated to owners and admins, which is the audience who can act on billing credit; a sidebar entry
 * would advertise it to members who cannot.
 *
 * Hierarchy, and where it departs from the reference design:
 *
 *   1. **Summary rail** — what is on the table, what is already earned. The reference shows four
 *      rewards and no totals, so there is no sense of the opportunity or of progress. This reuses the
 *      same `OrgUsageStatRail` the Usage and Analysis tabs use, which is most of why the page reads as
 *      native rather than bolted on.
 *   2. **The referral, featured** — it is worth 10× the others and is the only one with a real flow,
 *      so it gets its own panel with room for terms and history. The reference gives all four equal
 *      weight, which buries the one that matters.
 *   3. **The three one-click actions** — homogeneous, small, in a grid.
 *
 * No progress bars, no badges, no streaks: this is an account page for people spending real money,
 * and gamifying it would cheapen the product.
 */

export interface RewardsPanelProps {
  /** Sends the reader to the per-member ledger, where credits land on the bill. */
  onNavigateToUsage: () => void;
}

const REFERRAL_STATUS_META: Record<
  ReferralRecord['status'],
  { label: string; className: string }
> = {
  invited: { label: 'Invited', className: 'border-border/70 bg-muted/60 text-muted-foreground' },
  signed_up: { label: 'Signed up', className: 'border-warning/25 bg-warning/10 text-warning-strong' },
  credited: { label: 'Credited', className: 'border-success/25 bg-success/10 text-success-strong' },
  expired: { label: 'Expired', className: 'border-border/70 bg-muted/60 text-muted-foreground' },
};

const RECORD_DATE = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

function ReferralHistory({ referrals }: { referrals: ReferralRecord[] }) {
  if (referrals.length === 0) {
    return (
      <p className="oa-meta">
        No introductions yet. The people you refer and their status will appear here.
      </p>
    );
  }

  return (
    <ul className="space-y-1.5">
      {referrals.map((referral) => {
        const status = REFERRAL_STATUS_META[referral.status];
        return (
          /*
           * Two independently-truncating block lines rather than one line with a nested email span.
           * Nesting them meant the email escaped the parent's `truncate` and overflowed the card at
           * phone width — these addresses are long, and a referral list is mostly long addresses.
           */
          <li key={referral.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
            <span className="min-w-0 flex-1 basis-40">
              <span className="block truncate text-[13px]">
                {referral.company ?? referral.email}
              </span>
              {referral.company && (
                <span className="oa-meta block truncate">{referral.email}</span>
              )}
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <time dateTime={referral.submittedAt} className="oa-meta tabular-nums">
                {RECORD_DATE.format(new Date(referral.submittedAt))}
              </time>
              <span
                className={cn(
                  'rounded-full border px-2 py-0.5 text-[11px] font-medium',
                  status.className,
                )}
              >
                {status.label}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function RewardsPanel({ onNavigateToUsage }: RewardsPanelProps) {
  const { data, isLoading, isError, error, refetch } = useReferralProgram();
  const submitReferral = useSubmitReferral();
  const markPending = useMarkRewardPending();
  const [dialogOpen, setDialogOpen] = useState(false);

  const referralReward = useMemo(
    () => data?.rewards.find((reward) => reward.channel === 'referral') ?? null,
    [data],
  );
  const outboundRewards = useMemo(
    () => data?.rewards.filter((reward) => reward.channel !== 'referral') ?? [],
    [data],
  );

  const hasProgramme = (data?.rewards.length ?? 0) > 0;

  const stats: StatSpec[] = data
    ? [
        {
          label: 'Available to earn',
          value: formatRewardUsd(data.totals.availableUsd),
          hint: 'across all rewards',
        },
        {
          label: 'In progress',
          value: formatRewardUsd(data.totals.pendingUsd),
          hint: 'awaiting confirmation',
        },
        {
          label: 'Earned so far',
          value: formatRewardUsd(data.totals.earnedUsd),
          hint: 'credited to your account',
        },
      ]
    : [];

  /**
   * Outbound rewards open in a new tab and are recorded as pending.
   *
   * `noopener` matters: without it the opened page gets a handle on this window. The pending state is
   * recorded on the click, not on a return, because there is no way to observe whether the person
   * actually posted anything — and pretending otherwise is the one thing this feature must not do.
   */
  const handleOutbound = (rewardId: string, url: string | undefined) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    markPending.mutate(rewardId, {
      onSuccess: () => toast.success("Thanks — we'll confirm it shortly"),
    });
  };

  const handleReferralSubmit = async (submission: ReferralSubmission) => {
    await submitReferral.mutateAsync(submission);
  };

  if (isError) {
    return (
      <div className="oa-panel mb-3 mt-3 flex min-h-0 flex-1 flex-col">
        <OrgAdminErrorState
          title="Couldn't load rewards"
          error={error}
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="oa-toolbar flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2">
        <p className="oa-meta min-w-0 flex-1 basis-52 truncate">
          Earn credit toward your ncoder bill.
        </p>
        {REFERRAL_PROGRAM_IS_MOCK && (
          <Tooltip>
            <TooltipTrigger asChild>
              {/*
                Same visual treatment as the Analysis trend's badge, for the same reason: the reader
                must never have to guess whether what they are looking at is live.

                A DIFFERENT marker attribute, though. `data-oa-synthetic` means "a measurement here is
                fabricated", and the QA sweep fails a production build that renders one — a spend
                figure invented on a customer's screen is a serious bug. This is a catalogue of offers
                that is honestly labelled as not-yet-live, and it is *supposed* to show in production
                until the programme launches. Sharing one attribute would have made the sweep cry wolf.
              */}
              <span
                tabIndex={0}
                data-oa-preview="true"
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-warning/30 bg-warning/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning-strong"
              >
                <FlaskConical className="h-2.5 w-2.5" aria-hidden />
                Preview
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[300px] text-xs">
              The rewards programme is not live yet. Amounts and history shown here are a preview;
              introductions you send are not delivered.
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-3 p-[var(--oa-gutter)]">
          {/*
            The rail is hidden when there is no programme at all. Three tiles reading $0 / $0 / $0
            above an empty state tell the reader nothing and make the page look broken — the empty
            state alone is the honest answer. It still shows while loading, because then the totals
            are unknown rather than zero.
          */}
          {(isLoading || hasProgramme) && (
            <OrgUsageStatRail
              stats={stats}
              isLoading={isLoading}
              skeletonCount={3}
              ariaLabel="Reward totals"
            />
          )}

          {/* ── The featured reward ─────────────────────────────────────────── */}
          {isLoading ? (
            <section className="oa-panel p-[var(--oa-gutter)]" aria-busy="true" aria-label="Loading rewards">
              <div className="oa-skeleton h-3.5 w-44" />
              <div className="oa-skeleton mt-2.5 h-2.5 w-72" />
              <div className="oa-skeleton mt-4 h-8 w-36 rounded-md" />
            </section>
          ) : (
            referralReward && (
              <section className="oa-panel" aria-labelledby="rewards-referral-title">
                {/*
                  The copy column is capped and the reward sits immediately after it, rather than the
                  two being pushed to opposite edges by `justify-between`. At 1440 that left ~770px of
                  nothing between the offer and its price, so they read as two unrelated things; at
                  768, where the panel was narrow enough to pair them, the same layout looked right.
                  Capping makes every width behave like the one that already worked.
                */}
                <div className="p-[var(--oa-gutter)]">
                  {/*
                    The CTA sits BELOW this row rather than inside the copy column.
                    With it nested, the flex wrap on a phone produced copy → button → price: the
                    reader was asked to act before being told what the reward was. Pulling it out
                    makes the DOM order copy → reward → CTA, which is the right order at every width
                    and needs no breakpoint to achieve.
                  */}
                  <div className="flex flex-wrap items-start gap-x-10 gap-y-3">
                    <div className="min-w-0 flex-1 basis-64 sm:max-w-[520px]">
                      <h3 id="rewards-referral-title" className="oa-title">
                        {referralReward.title}
                      </h3>
                      <p className="oa-meta mt-1">{referralReward.description}</p>
                      <p className="mt-2 text-[13px] text-foreground/90">{referralReward.terms}</p>
                    </div>

                  {/*
                    The amount, given real weight — this is the one place on the page where the reward
                    outranks the CTA, because $250 is the reason to read any of this. Still a figure in
                    a panel, not a coloured prize block.
                  */}
                  <div className="shrink-0">
                    <p className="oa-micro">Reward</p>
                    {/*
                      32px: this is the largest opportunity on the tab and there is nothing else here
                      competing to be the hero, so at 26px it was only marginally louder than the
                      summary tiles above it. It stays smaller than the Analysis tab's spend hero,
                      which is a measurement rather than an offer.
                    */}
                    <p className="mt-0.5 text-[32px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                      {formatRewardUsd(referralReward.amountUsd)}
                    </p>
                    <p className="oa-meta mt-1">
                      {referralReward.kind === 'credit' ? 'bill credit, each' : 'gift card'}
                    </p>
                    </div>
                  </div>

                  {/*
                    One CTA, not two. "See where credit lands" sat beside the primary action at
                    near-equal weight, which is the classic way to weaken a CTA — and its label was
                    vague on a page that is entirely about credit. The link to the ledger lives in the
                    history header below, where someone looking for it will be.
                  */}
                  <div className="mt-4">
                    <Button type="button" size="sm" className="h-8" onClick={() => setDialogOpen(true)}>
                      <Gift className="h-4 w-4" aria-hidden />
                      {referralReward.actionLabel}
                    </Button>
                  </div>
                </div>

                {data && (
                  <div className="border-t border-border/50 bg-muted/20 p-[var(--oa-gutter)]">
                    {/*
                      One capped content column for the whole section, so the label, the ledger link
                      and every row share a left and a right edge. Capping only the list left "View
                      bill" stranded 600px to the right of the rows it belongs to.
                    */}
                    <div className="max-w-[820px]">
                      <div className="mb-2.5 flex items-center justify-between gap-3">
                        <p className="oa-micro">Your introductions</p>
                        <button
                          type="button"
                          onClick={onNavigateToUsage}
                          className="oa-meta inline-flex min-h-6 items-center rounded px-1 transition-colors hover:text-foreground"
                        >
                          View bill
                        </button>
                      </div>
                      <ReferralHistory referrals={data.referrals} />
                    </div>
                  </div>
                )}
              </section>
            )
          )}

          {/* ── The one-click rewards ───────────────────────────────────────── */}
          {!isLoading && outboundRewards.length > 0 && (
            <section aria-labelledby="rewards-quick-title">
              <h3 id="rewards-quick-title" className="oa-micro mb-2 px-0.5">
                One-click rewards
              </h3>
              {/*
                `auto-fit` with a minimum track, not a fixed column count: this panel sits inside a
                collapsible shell, so the number that fits depends on the panel's width and not the
                window's.
              */}
              <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(240px,100%),1fr))]">
                {outboundRewards.map((reward) => (
                  <RewardCard
                    key={reward.id}
                    reward={reward}
                    onAction={(target) => handleOutbound(target.id, target.url)}
                  />
                ))}
              </div>
            </section>
          )}

          {!isLoading && !hasProgramme && (
            <div className="oa-panel">
              <OrgAdminEmptyState
                icon={Gift}
                title="No rewards available yet"
                description="When a referral programme is available for your organization, the ways to earn credit will appear here."
              />
            </div>
          )}
        </div>
      </div>

      {referralReward && data && (
        <ReferralDialog
          open={dialogOpen}
          shareUrl={data.shareUrl}
          rewardLabel={rewardLabel(referralReward)}
          isSubmitting={submitReferral.isPending}
          onSubmit={handleReferralSubmit}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </div>
  );
}
